import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  useToast,
  Image,
  Box,
  Progress,
  Text,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";

const AlbumModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [labels, setLabels] = useState([]);
  const [musicians, setMusicians] = useState([]);
  const [albumCover, setAlbumCover] = useState(null);
  const [albumCoverPreview, setAlbumCoverPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");

  const [formData, setFormData] = useState({
    labelId: "",
    musicianId: "",
    name_album_kr: "",
    name_album_en: "",
    description: "",
    salesType: "발매 음반",
  });

  useEffect(() => {
    fetchUserData();
  }, [user]);

  const fetchUserData = async () => {
    try {
      // 레이블 데이터 가져오기
      const { data: labelData, error: labelError } = await supabase
        .from("label")
        .select("*")
        .eq("user", user.email);

      if (labelError) throw labelError;
      setLabels(labelData);

      // 뮤지션 데이터 가져오기
      const { data: musicianData, error: musicianError } = await supabase
        .from("musician")
        .select("*")
        .eq("user", user.email);

      if (musicianError) throw musicianError;
      setMusicians(musicianData);
    } catch (error) {
      console.error("데이터 가져오기 실패:", error);
      toast({
        title: "데이터 로딩 실패",
        description: "라벨과 뮤지션 정보를 가져오는데 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // jpg만 허용
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "jpg" && ext !== "jpeg") {
        toast({
          title: "이미지 파일 형식 오류",
          description: "JPG 파일만 업로드할 수 있습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        e.target.value = "";
        return;
      }
      setAlbumCover(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAlbumCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadFile = async (file, path) => {
    try {
      const fileExt = file.name.split(".").pop()?.toLowerCase() || path;
      const timestamp = new Date().getTime();
      const fileName = `${path}_IMG_${timestamp}.${fileExt}`;
      const filePath = `${fileName}`;

      setUploadStatus(`"${file.name}" 파일 업로드 중...`);

      const { data, error: urlError } = await supabase.storage
        .from("image")
        .createSignedUploadUrl(filePath);

      if (urlError || !data?.signedUrl) {
        console.error("URL 발급 오류:", urlError);
        throw new Error("Signed URL 발급 실패");
      }

      const signedUrl = data.signedUrl;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type || "image/jpeg");

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            console.error("업로드 실패 상세:", {
              status: xhr.status,
              statusText: xhr.statusText,
              response: xhr.response,
            });
            reject(new Error(`업로드 실패: ${xhr.status} ${xhr.statusText}`));
          }
        };
        xhr.onerror = () => {
          console.error("네트워크 오류 상세:", xhr);
          reject(new Error("네트워크 오류가 발생했습니다."));
        };
        xhr.send(file);
      });

      const { data: publicUrlData } = supabase.storage
        .from("image")
        .getPublicUrl(filePath);

      return publicUrlData?.publicUrl;
    } catch (error) {
      console.error("파일 업로드 중 오류 발생:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    const missingFields = [];
    if (!formData.labelId) missingFields.push("레이블");
    if (!formData.musicianId) missingFields.push("뮤지션");
    if (!formData.name_album_kr) missingFields.push("앨범명(국문)");
    if (!formData.name_album_en) missingFields.push("앨범명(영문)");
    if (!albumCover) missingFields.push("앨범 커버");

    if (missingFields.length > 0) {
      toast({
        title: "필수 항목 누락",
        description: `${missingFields.join(", ")}`,
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // jpg 파일만 허용
    if (albumCover) {
      const ext = albumCover.name.split(".").pop().toLowerCase();
      if (ext !== "jpg" && ext !== "jpeg") {
        toast({
          title: "이미지 파일 형식 오류",
          description: "JPG 파일만 업로드할 수 있습니다.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    setIsLoading(true);
    try {
      const today = new Date();
      const year = today.getFullYear().toString().slice(-2);
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      const hours = String(today.getHours()).padStart(2, "0");
      const minutes = String(today.getMinutes()).padStart(2, "0");
      const seconds = String(today.getSeconds()).padStart(2, "0");

      const albumCode = `${year}${month}${day}U${hours}${minutes}${seconds}`;

      setUploadStatus("앨범 커버 이미지 업로드 중...");
      const coverUrl = await uploadFile(
        albumCover,
        `album/${user?.email}/${albumCode}`
      );

      // 앨범 데이터 저장
      const { data, error } = await supabase.from("album").insert([
        {
          user: user.email,
          label: formData.labelId,
          musician: formData.musicianId,
          name_album_kr: formData.name_album_kr,
          name_album_en: formData.name_album_en,
          description: formData.description,
          sales_type: formData.salesType,
          nation: "대한민국",
          cover: coverUrl,
          registered_at: new Date().toISOString(),
          status_musician: "[뮤지션] 작성중",
          type_registeration: "Publish",
          code_album: albumCode,
        },
      ]);

      if (error) throw error;

      toast({
        title: "앨범 등록 성공",
        description: "앨범이 성공적으로 등록되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error("앨범 등록 실패:", error);
      toast({
        title: "앨범 등록 실패",
        description: error.message || "앨범 등록 중 오류가 발생했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      setUploadStatus("");
    }
  };

  const handleClose = () => {
    setFormData({
      labelId: "",
      musicianId: "",
      name_album_kr: "",
      name_album_en: "",
      description: "",
      salesType: "발매 음반",
    });
    setAlbumCover(null);
    setAlbumCoverPreview(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay backdropFilter="blur(10px)" />
      <ModalContent maxW="800px" borderRadius="xl" boxShadow="xl">
        <ModalHeader
          bg="blue.50"
          borderTopRadius="xl"
          py={4}
          fontSize="xl"
          fontWeight="bold"
          color="blue.700"
        >
          앨범 등록
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody p={6}>
          <VStack spacing={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium" color="gray.700">
                앨범 커버
              </FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                borderRadius="md"
                p={1}
              />
              {albumCoverPreview && (
                <Box mt={2}>
                  <Image
                    src={albumCoverPreview}
                    alt="Album cover preview"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                  />
                </Box>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>레이블</FormLabel>
              <Select
                placeholder="레이블 선택"
                value={formData.labelId}
                onChange={(e) =>
                  setFormData({ ...formData, labelId: e.target.value })
                }
              >
                {labels.map((label) => (
                  <option key={label.id} value={label.id}>
                    {label.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>뮤지션</FormLabel>
              <Select
                placeholder="뮤지션 선택 (타이틀 곡의 뮤지션과 동일)"
                value={formData.musicianId}
                onChange={(e) =>
                  setFormData({ ...formData, musicianId: e.target.value })
                }
              >
                {musicians.map((musician) => (
                  <option key={musician.id} value={musician.id}>
                    {musician.name_musician}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* 앨범명 (국문/영문) */}
            <Box width="100%">
              <FormLabel fontWeight="medium" color="gray.700">
                앨범명{" "}
                <Box as="span" color="red.500" display="inline">
                  *
                </Box>
              </FormLabel>
              <Text color="gray.500" fontSize="sm" mb={2}>
                국문에도 영어가 가능합니다. 국문 앨범명은 한국 발매 이름이고,
                영문 앨범명은 해외 발매 이름이 됩니다.
              </Text>
              <Box display="flex" gap={2}>
                <Input
                  placeholder="국문 앨범명"
                  value={formData.name_album_kr}
                  onChange={(e) =>
                    setFormData({ ...formData, name_album_kr: e.target.value })
                  }
                  isRequired
                  required
                />
                <Input
                  placeholder="영문 앨범명"
                  value={formData.name_album_en}
                  onChange={(e) =>
                    setFormData({ ...formData, name_album_en: e.target.value })
                  }
                  isRequired
                  required
                />
              </Box>
            </Box>

            <FormControl>
              <FormLabel>앨범 소개</FormLabel>
              <Textarea
                placeholder={`음원 사이트에 앨범 소개란에 기재되는 내용이에요.

앨범에 대한 소개와 참여한 아티스트들의 크레딧을 기재할 수 있어요.`}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={4}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>판매 유형</FormLabel>
              <RadioGroup
                value={formData.salesType}
                onChange={(value) =>
                  setFormData({ ...formData, salesType: value })
                }
              >
                <Box display="flex" gap={6} alignItems="center">
                  <Radio colorScheme="blue" value="발매 음반">
                    음반 유통/발매 신청
                  </Radio>
                  <Radio value="판매 음반" isDisabled colorScheme="gray">
                    음반 판매 (예정)
                  </Radio>
                </Box>
              </RadioGroup>
            </FormControl>

            {isLoading && (
              <Box width="100%" p={4} bg="blue.50" borderRadius="lg">
                <Text mb={2} fontWeight="medium" color="blue.700">
                  {uploadStatus}
                </Text>
                <Progress
                  value={uploadProgress}
                  size="sm"
                  colorScheme="blue"
                  borderRadius="full"
                  hasStripe
                  isAnimated
                />
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter p={4} borderTopWidth={1} borderColor="gray.200">
          <Button variant="ghost" mr={3} onClick={handleClose}>
            취소
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="등록 중..."
          >
            등록
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AlbumModal;
